import { Component, Input, Output, EventEmitter, output, input, inject, computed } from '@angular/core';
import {
  DisabledEngravingModalData,
  EngravingModalService,
  EngravingUtilitiesService,
  ProcessedProduct,
  UserService,
} from '@core';
import { AppleCareModalService } from '@core';
import type { ProductCart } from '@models';
import { calculateProductPoints } from '@models';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { CartOldPriceComponent } from '../../../shared/src/lib/cart-old-price/cart-old-price.component';
import { AlertMessageComponent } from '../../../shared/src/lib/alert-message/alert-message.component';
import { CounterComponent } from '../../../shared/src/lib/counter/counter.component';
import { TrackClickDirective, EventTrackingService, TRACKABLE_SCREEN_NAME } from '@analytics';
import { AccessibleClickDirective } from '../../../shared/src/lib/directives';
import { LightCarouselComponent } from '@light-carousel';
import { PageNameService } from '../../../adobe-analytics/src/lib/services/page-name.service';

export interface AppleCareItem {
  id: string;
  productName: string;
  price: number;
  points?: number;
  oldPoints?: number; // Points at standard rate for strikethrough display
  isAdded: boolean;
  brand: string;
  model: string;
  productId: number;
  lineNumber?: number;
}
@Component({
    selector: 'app-apple-care-listing-product',
    templateUrl: './apple-care-listing-product.component.html',
    styleUrls: ['./apple-care-listing-product.component.scss'],
    imports: [
        CommonModule,
        AlertMessageComponent,
        AccessibleClickDirective,
        CartOldPriceComponent,
        CounterComponent,
        MatIcon,
        LightCarouselComponent,
        TrackClickDirective,
    ]
})
export class AppleCareListingProductComponent {
  // --- AppleCare Inputs/Outputs (Restored) ---
  @Input() appleCareItem: AppleCareItem | null = null;
  @Input() showControls = true;
  @Input() isOrderReview = false;
  @Input() isMiniCart = false;
  // Outputs
  @Output() addAppleCare = new EventEmitter<{ appleCareItem: AppleCareItem; parentLineNumber: number }>();
  @Output() removeAppleCare = new EventEmitter<{ appleCareItem: AppleCareItem; parentLineNumber: number }>();
  @Output() deleteEngraving = new EventEmitter<{ productId: number }>();
  @Output() remove = new EventEmitter<{ productId: number }>();


  // --- Quantity Restriction Inputs ---
  @Input() disableAppleCare = false;
  @Input() disableEngraving = false;

  /**
   * FCR 19.1 — optional monthly-limit warning shown under the card (AC05b), same placement as the
   * merch tile (`lib-cart-item`). Apple spreads a SKU over one line per unit, so both cart hosts
   * pass the same per-SKU message to every tile of that SKU.
   * The consolidated AC07b removal message is a separate, cart-level banner
   * (`lib-cart-warning-banners`). Empty/undefined hides it.
   */
  @Input() limitMessage?: string;

  appleCareLoading = input<boolean>(false);
  hasAppleCareAdded = computed(() => this.appleCareItem?.isAdded ?? false);


  vm = computed(() => {
    const product = this.product();
    const price = product?.price ?? 0;
    const discountedPrice = product?.discountedPrice ?? 0;
    const pointConversionRate = product?.pointConversionRate;

    const pricePoints = calculateProductPoints(price, pointConversionRate, this.userService.conversionRate());
    const discountedPricePoints = discountedPrice
      ? calculateProductPoints(discountedPrice, pointConversionRate, this.userService.conversionRate())
      : { newPoints: 0, oldPoints: null, hasSpecialRate: false };

    // Determine what to show for old/new points
    const hasSpecialRate = pricePoints.hasSpecialRate;
    const isPromo = product?.isPromo;

    let displayNewPoints: number;
    let displayOldPoints: number | null;
    let displayNewPrice: number;
    let displayOldPrice: number | null;

    if (isPromo && discountedPrice) {
      // Price discount: show discounted price/points, old regular price/points
      displayNewPoints = discountedPricePoints.newPoints;
      displayNewPrice = discountedPrice;
      displayOldPrice = price;
      // Old points could be at special rate or standard rate
      displayOldPoints = hasSpecialRate ? pricePoints.oldPoints : pricePoints.newPoints;
    } else {
      // No price discount
      displayNewPoints = pricePoints.newPoints;
      displayNewPrice = price;
      displayOldPrice = null;
      // If special rate, show standard rate points as old
      displayOldPoints = hasSpecialRate ? pricePoints.oldPoints : null;
    }

    return {
      product,
      points: pricePoints.newPoints,
      discountedPoints: discountedPricePoints.newPoints,
      smallImagesForCards: product?.images,
      // New display values
      displayNewPoints,
      displayOldPoints,
      displayNewPrice,
      displayOldPrice,
      hasSpecialRate,
      shouldShowOldPoints: displayOldPoints !== null,
    };
  });

  engravingLines = computed(() => {
    const text = this.engravingText();
    if (!text) {
      return [];
    }
    return text.split('\n');
  });

  // --- Core Inputs ---
  product = input<ProductCart | ProcessedProduct>();
  loading = input<boolean>();
  mode = input<'mobile' | 'tablet'>();

  hasAppleCare = input<boolean>(false);
  isEngravable = input<boolean>(false);
  engravingText = input<string | null>();

  editEngraving = output<void>();

  // Services
  userService = inject(UserService);
  private engravingModalService = inject(EngravingModalService);
  private engravingService = inject(EngravingUtilitiesService);
  private appleCareModalService = inject(AppleCareModalService);
  private eventTrackingService = inject(EventTrackingService);
  private pageNameService = inject(PageNameService);
  private trackableScreenName = inject(TRACKABLE_SCREEN_NAME, { optional: true });

  onRemove(id: number): void {
    this.remove.emit({ productId: id });
  }

  onEditEngraving(): void {
    if (this.disableEngraving) {
return;
}
    this.editEngraving.emit();
  }

  onDeleteEngraving(): void {
    const prod = this.product();
    if (!prod) {return;}
    this.deleteEngraving.emit({ productId: prod.productId });
  }

  /**
   * Add AppleCare - always adds exactly 1
   */
  onAddAppleCare(): void {
  if (this.disableAppleCare || this.isAppleCareActionDisabled()) {
    return;
  }
  if (this.appleCareItem) {
    const parentProduct = this.product();
    // validate that lineNumber is a positive number
    if (!parentProduct?.lineNumber || parentProduct.lineNumber <= 0) {
      console.error('Cannot add AppleCare: Parent product lineNumber is missing or invalid', {
        parentProduct,
        lineNumber: parentProduct?.lineNumber,
      });
      return;
    }

    this.eventTrackingService.trackEvent({
      eventType: 'modal',
      component: 'applecare',
      item: 'addtocart',
      screenName: this.trackableScreenName || this.pageNameService.activeScreenName(),
      previousScreenName: null,
      timestamp: new Date().toISOString(),
    });
    // Emit both the AppleCare item and the parent's lineNumber (validated above)
    this.addAppleCare.emit({
      appleCareItem: this.appleCareItem,
      parentLineNumber: parentProduct.lineNumber
    });
  }
}

  /**
   * Remove AppleCare - removes the 1:1 linked item
   */
  onRemoveAppleCare(): void {
  if (this.appleCareItem && !this.isAppleCareActionDisabled()) {
    const parentProduct = this.product();
    if (!parentProduct?.lineNumber) {
      console.error('Parent product lineNumber is missing');
      return;
    }
    this.eventTrackingService.trackEvent({
      eventType: 'modal',
      component: 'applecare',
      item: 'removefromcart',
      screenName: this.trackableScreenName || this.pageNameService.activeScreenName(),
      previousScreenName: null,
      timestamp: new Date().toISOString(),
    });
    this.removeAppleCare.emit({ 
      appleCareItem: this.appleCareItem,
      parentLineNumber: parentProduct.lineNumber 
    });
  }
}

  onLearnMoreAppleCare(): void {
    if (!this.appleCareItem) {
      return;
    }

    try {
      void this.appleCareModalService.fetchAppleCareData(
        `${this.appleCareItem.brand} ${this.appleCareItem.model}`
      ).then(modalData => {
        this.appleCareModalService['modalDataSubject'].next(modalData);
        this.appleCareModalService['isModalOpenSubject'].next(true);
      });
    } catch (error) {
      console.error('Failed to load AppleCare information:', error);
    }
  }

  // Disabled state checks
  isAppleCareActionDisabled(): boolean {
    return this.appleCareLoading() || this.loading() || this.disableAppleCare;
  }

  isEngravingActionDisabled(): boolean {
    return this.loading() || this.disableEngraving;
  }

  shouldDisableQuantityControls(): boolean {
    // Quantity controls for the main product, not AppleCare
    return false;
  }

  // Display helpers
  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  formatPoints(points: number): string {
    return points.toLocaleString();
  }

  /**
   * SIMPLIFIED: Button text for 1:1 AppleCare
   */
  getAppleCareButtonText(): string {
    if (this.isAppleCareActionDisabled()) {
      if (this.disableAppleCare) {
        return 'Not available (qty > 1)';
      }
      return 'Processing...';
    }
    
    // Simple: Either add or it's already added
    return this.hasAppleCareAdded() ? 'AppleCare+ Added' : 'Add AppleCare+';
  }

  getEngravingButtonText(): string {
    if (this.isEngravingActionDisabled()) {
      if (this.disableEngraving) {
        return 'Not available (qty > 1)';
      }
      return 'Loading...';
    }
    return this.engravingText() ? 'Edit' : 'Add';
  }

  // Section visibility
  shouldShowAppleCareSection(): boolean {
    return !!this.appleCareItem && (!this.disableAppleCare || !!this.appleCareItem.isAdded);
  }

  shouldShowEngravingSection(): boolean {
    return this.isEngravable() && !this.disableEngraving;
  }

  shouldShowAddEngravingSection(): boolean {
    return this.isEngravable() && !this.engravingText() && !this.disableEngraving;
  }

  // Type guards
  isProcessedProduct(product: ProductCart | ProcessedProduct): product is ProcessedProduct {
    return 'engravingText' in product && 'isEngravable' in product;
  }

  hasEngraving(product: ProductCart | ProcessedProduct): boolean {
    if (this.isProcessedProduct(product)) {
      return this.engravingService.hasEngraving(product);
    }
    return false;
  }

  viewEngravingDetails(product: ProductCart | ProcessedProduct): void {
    if (!this.isProcessedProduct(product)) {return;}
    if (!product.engravingText) {return;}

    const disabledModalData: DisabledEngravingModalData = {
      product: product,
      engravingText: product.engravingText,
    };

    this.engravingModalService.openDisabledModal(disabledModalData);
  }
}